import React, { useState } from "react";
import { Button, Card, Spinner } from "../components/atoms";

interface UploadSlot {
  id: string;
  uploadUrl: string;
  publicUrl: string;
  successUrl: string;
  cancelUrl: string;
  expiresAt: string;
}

interface ProcessingResult {
  identified: boolean;
  confidence: number;
  food: {
    name: string;
    category: string;
    cuisineType: string | null;
  } | null;
  nutrition: {
    estimatedCalories: number;
    confidence: string;
    macros: {
      proteinGrams: number;
      carbsGrams: number;
      fatGrams: number;
      fiberGrams: number | null;
    };
  } | null;
  components: Array<{
    name: string;
    category: string;
    estimatedPortion: string;
    isMainIngredient: boolean;
  }>;
  barcode: {
    detected: boolean;
    value: string | null;
    format: string | null;
  };
  imageQuality: {
    shootingAngle: number;
    angleQuality: string;
    lighting: string;
    focus: string;
    recommendations: string[];
  };
  warnings: string[];
}

export const FoodImageUploadPage: React.FC = () => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [uploadSlot, setUploadSlot] = useState<UploadSlot | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [result, setResult] = useState<ProcessingResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      setError("Please select an image file");
      return;
    }

    setSelectedFile(file);
    setPreviewUrl(URL.createObjectURL(file));
    setError(null);
  };

  const handleRequestSlot = async () => {
    if (!selectedFile) return;

    setIsUploading(true);
    setError(null);

    try {
      const apiUrl = import.meta.env.VITE_API_URL || "/api";
      const response = await fetch(`${apiUrl}/food/upload/slot`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "x-test-user-id": "1", // For testing
        },
        body: JSON.stringify({
          mimeType: selectedFile.type,
          mealType: "lunch",
        }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP ${response.status}: ${errorText}`);
      }

      const data = await response.json();
      setUploadSlot(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to request upload slot");
    } finally {
      setIsUploading(false);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile || !uploadSlot) return;

    setIsUploading(true);
    setError(null);

    try {
      // Step 1: Upload file to R2 via uploadUrl
      console.log("Uploading to:", uploadSlot.uploadUrl);

      const uploadResponse = await fetch(uploadSlot.uploadUrl, {
        method: "PUT",
        body: selectedFile,
        headers: {
          "Content-Type": selectedFile.type,
        },
      });

      if (!uploadResponse.ok) {
        throw new Error(`Upload failed: ${uploadResponse.status} ${uploadResponse.statusText}`);
      }

      console.log("Upload successful, notifying backend...");
      setIsUploading(false);

      // Step 2: Notify backend via success callback (GET request)
      const successResponse = await fetch(uploadSlot.successUrl);

      if (!successResponse.ok) {
        throw new Error(`Success callback failed: ${successResponse.status}`);
      }

      console.log("Job queued for AI processing");
      setIsProcessing(true);

      // Step 3: Poll for results from R2
      // Results are saved as <key>._.json in R2
      const resultsUrl = uploadSlot.publicUrl + "._.json";
      let attempts = 0;
      const maxAttempts = 60; // 60 seconds max

      const pollInterval = setInterval(async () => {
        attempts++;

        try {
          const response = await fetch(resultsUrl);

          if (response.ok) {
            const data = await response.json();
            console.log("AI processing complete!", data);

            setResult(data.aiAnalysis as ProcessingResult);
            setIsProcessing(false);
            clearInterval(pollInterval);
          } else if (attempts >= maxAttempts) {
            throw new Error("Processing timeout - results not available after 60s");
          }
        } catch (pollError) {
          if (attempts >= maxAttempts) {
            setError("AI processing timed out. Check backend logs.");
            setIsProcessing(false);
            clearInterval(pollInterval);
          }
          // Continue polling
        }
      }, 1000); // Poll every second
    } catch (err) {
      console.error("Upload error:", err);
      setError(err instanceof Error ? err.message : "Upload failed");
      setIsUploading(false);
      setIsProcessing(false);
    }
  };

  const handleReset = () => {
    setSelectedFile(null);
    setPreviewUrl(null);
    setUploadSlot(null);
    setResult(null);
    setError(null);
    setIsProcessing(false);
  };

  return (
    <div className="container mx-auto px-4 py-8 max-w-6xl">
      <h1 className="text-3xl font-bold mb-8">Food Image Upload & AI Analysis</h1>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Upload Section */}
        <Card className="p-6">
          <h2 className="text-xl font-semibold mb-4">Upload Food Image</h2>

          <div className="space-y-4">
            <div>
              <label
                htmlFor="file-upload"
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                Select Image
              </label>
              <input
                id="file-upload"
                type="file"
                accept="image/*"
                onChange={handleFileSelect}
                className="block w-full text-sm text-gray-500
                  file:mr-4 file:py-2 file:px-4
                  file:rounded-full file:border-0
                  file:text-sm file:font-semibold
                  file:bg-blue-50 file:text-blue-700
                  hover:file:bg-blue-100"
              />
            </div>

            {previewUrl && (
              <div className="mt-4">
                <img
                  src={previewUrl}
                  alt="Preview"
                  className="w-full h-64 object-cover rounded-lg border-2 border-gray-200"
                />
              </div>
            )}

            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
                {error}
              </div>
            )}

            <div className="flex gap-2">
              {!uploadSlot && (
                <Button
                  onClick={handleRequestSlot}
                  disabled={!selectedFile || isUploading}
                  className="flex-1"
                >
                  {isUploading ? <Spinner className="mr-2" /> : null}
                  Request Upload Slot
                </Button>
              )}

              {uploadSlot && !result && (
                <Button
                  onClick={handleUpload}
                  disabled={isUploading || isProcessing}
                  className="flex-1"
                >
                  {isUploading || isProcessing ? <Spinner className="mr-2" /> : null}
                  {isProcessing ? "Processing..." : "Upload & Analyze"}
                </Button>
              )}

              {(uploadSlot || result) && (
                <Button onClick={handleReset} variant="secondary">
                  Reset
                </Button>
              )}
            </div>

            {uploadSlot && (
              <div className="mt-4 p-4 bg-gray-50 rounded text-sm">
                <p className="font-semibold mb-2">Upload Slot Created:</p>
                <div className="space-y-1 text-xs font-mono">
                  <p>
                    <span className="text-gray-600">ID:</span> {uploadSlot.id}
                  </p>
                  <p>
                    <span className="text-gray-600">Expires:</span>{" "}
                    {new Date(uploadSlot.expiresAt).toLocaleString()}
                  </p>
                </div>
              </div>
            )}
          </div>
        </Card>

        {/* Results Section */}
        <Card className="p-6">
          <h2 className="text-xl font-semibold mb-4">AI Analysis Results</h2>

          {isProcessing && (
            <div className="flex flex-col items-center justify-center py-12">
              <Spinner className="w-12 h-12 mb-4" />
              <p className="text-gray-600">Analyzing image with AI...</p>
            </div>
          )}

          {!isProcessing && !result && (
            <div className="flex items-center justify-center py-12 text-gray-400">
              <p>Upload an image to see AI analysis</p>
            </div>
          )}

          {result && (
            <div className="space-y-4">
              {/* Food Info */}
              {result.food && (
                <div className="bg-green-50 p-4 rounded-lg">
                  <h3 className="font-semibold text-green-900 mb-2">
                    {result.food.name}
                  </h3>
                  <div className="text-sm text-green-700">
                    <p>Category: {result.food.category}</p>
                    {result.food.cuisineType && (
                      <p>Cuisine: {result.food.cuisineType}</p>
                    )}
                    <p>Confidence: {(result.confidence * 100).toFixed(0)}%</p>
                  </div>
                </div>
              )}

              {/* Nutrition */}
              {result.nutrition && (
                <div className="bg-blue-50 p-4 rounded-lg">
                  <h3 className="font-semibold text-blue-900 mb-2">
                    Nutrition ({result.nutrition.confidence} confidence)
                  </h3>
                  <div className="grid grid-cols-2 gap-2 text-sm text-blue-700">
                    <p>Calories: {result.nutrition.estimatedCalories} kcal</p>
                    <p>Protein: {result.nutrition.macros.proteinGrams}g</p>
                    <p>Carbs: {result.nutrition.macros.carbsGrams}g</p>
                    <p>Fat: {result.nutrition.macros.fatGrams}g</p>
                  </div>
                </div>
              )}

              {/* Components */}
              {result.components.length > 0 && (
                <div className="bg-purple-50 p-4 rounded-lg">
                  <h3 className="font-semibold text-purple-900 mb-2">
                    Ingredients ({result.components.length})
                  </h3>
                  <div className="space-y-1">
                    {result.components.map((comp, idx) => (
                      <div key={idx} className="text-sm text-purple-700 flex justify-between">
                        <span>
                          {comp.isMainIngredient ? "★ " : ""}
                          {comp.name}
                        </span>
                        <span className="text-purple-500">{comp.estimatedPortion}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Image Quality */}
              <div className="bg-orange-50 p-4 rounded-lg">
                <h3 className="font-semibold text-orange-900 mb-2">Image Quality</h3>
                <div className="text-sm text-orange-700 space-y-1">
                  <p>
                    Angle: {result.imageQuality.shootingAngle}° (
                    {result.imageQuality.angleQuality})
                  </p>
                  <p>Lighting: {result.imageQuality.lighting}</p>
                  <p>Focus: {result.imageQuality.focus}</p>
                  {result.imageQuality.recommendations.length > 0 && (
                    <div className="mt-2">
                      <p className="font-medium">Recommendations:</p>
                      <ul className="list-disc list-inside mt-1">
                        {result.imageQuality.recommendations.map((rec, idx) => (
                          <li key={idx}>{rec}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              </div>

              {/* Barcode */}
              {result.barcode.detected && (
                <div className="bg-yellow-50 p-4 rounded-lg">
                  <h3 className="font-semibold text-yellow-900 mb-2">
                    Barcode Detected
                  </h3>
                  <div className="text-sm text-yellow-700">
                    <p>Value: {result.barcode.value}</p>
                    <p>Format: {result.barcode.format}</p>
                  </div>
                </div>
              )}

              {/* Warnings */}
              {result.warnings.length > 0 && (
                <div className="bg-red-50 p-4 rounded-lg">
                  <h3 className="font-semibold text-red-900 mb-2">Warnings</h3>
                  <ul className="list-disc list-inside text-sm text-red-700">
                    {result.warnings.map((warning, idx) => (
                      <li key={idx}>{warning}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </Card>
      </div>

      {/* Technical Details */}
      <Card className="mt-8 p-6">
        <h2 className="text-xl font-semibold mb-4">Technical Details</h2>
        <div className="space-y-4 text-sm">
          <div>
            <h3 className="font-semibold mb-2">How it works:</h3>
            <ol className="list-decimal list-inside space-y-1 text-gray-700">
              <li>Select food image from your device</li>
              <li>Request upload slot (generates signed URLs)</li>
              <li>Upload image directly to Cloudflare R2</li>
              <li>Notify backend via success callback</li>
              <li>Job queued in Cloudflare Queue</li>
              <li>Worker processes image with Gemini 2.0 Flash</li>
              <li>AI extracts nutritional information</li>
              <li>Results saved and displayed</li>
            </ol>
          </div>

          <div className="bg-gray-50 p-4 rounded">
            <h3 className="font-semibold mb-2">Infrastructure:</h3>
            <div className="font-mono text-xs space-y-1 text-gray-600">
              <p>✅ R2 Bucket: treel-food-images-dev</p>
              <p>✅ Queue: food-image-processing-dev</p>
              <p>✅ AI Model: gemini-2.0-flash</p>
              <p>✅ Package: @techery/asset-vision v1.0.0</p>
            </div>
          </div>

          {uploadSlot && (
            <div className="bg-blue-50 p-4 rounded">
              <h3 className="font-semibold mb-2">Upload Slot Details:</h3>
              <div className="font-mono text-xs space-y-1 text-gray-600 break-all">
                <p>
                  <span className="font-semibold">Upload URL:</span>{" "}
                  {uploadSlot.uploadUrl}
                </p>
                <p>
                  <span className="font-semibold">Public URL:</span>{" "}
                  {uploadSlot.publicUrl}
                </p>
                <p>
                  <span className="font-semibold">Success URL:</span>{" "}
                  {uploadSlot.successUrl}
                </p>
              </div>
            </div>
          )}
        </div>
      </Card>
    </div>
  );
};
